import React from 'react';
import { useFormikContext, FieldArray, FormikProps } from 'formik';
import { useTranslation } from 'react-i18next';

import IconButton from '@material-ui/core/IconButton';
import DeleteIcon from '@material-ui/icons/Delete';
import Button from '@material-ui/core/Button';
import Grid from '@material-ui/core/Grid';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import TextField from '@material-ui/core/TextField';

import FormTextField from 'components/FormFields/FormTextField';
import FormSelectField from 'components/FormFields/FormSelectField';
import FormNumberField from 'components/FormFields/FormNumberField';

import { defaultValue, RetirementInterface } from 'stores/workflowStores/finances/retirement/model';
import { useTwoColumns } from 'hooks/useTwoColumns';
import { Currency } from 'enums/index';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    description: {
      maxWidth: '90%',
      flexBasis: '90%',
    },
    deleteButton: {
      maxWidth: '10%',
      flexBasis: '10%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    nextButton: {
      marginTop: theme.spacing(2),
    },
  }),
);

interface Props {
  calcTotalIncome: (formikProps: FormikProps<RetirementInterface>) => void;
  currency: Currency;
  accountId: string;
}

const OtherIncome: React.FC<Props> = ({ calcTotalIncome, currency, accountId }) => {
  const formikProps = useFormikContext<RetirementInterface>();
  const { values } = formikProps;
  const { t } = useTranslation();
  const classes = useStyles();
  const { twoColumns } = useTwoColumns();
  const translatedCurrency = t(`currency_${currency}`);

  return (
    <FieldArray
      name="extraIncome"
      render={(arrayHelpers) => (
        <>
          <div className={twoColumns} style={{ width: '100%', margin: 8 }}>
            {values.extraIncome!.map((_income, index) => (
              <React.Fragment key={`otherIncome_${index}`}>
                <Grid item container style={{ marginBottom: 16 }}>
                  <Grid item xs={6}>
                    <FormSelectField
                      name={`extraIncome[${index}].type`}
                      label={t('finQuestionnary_pension_incomeKind')}
                      options={values.options?.extraIncomeType.map((type) => ({
                        value: type,
                        label: t(`finQuestionnary_extraIncome_${type}`),
                      }))}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <FormNumberField
                      name={`extraIncome[${index}].value.value`}
                      label={t('finQuestionnary_pension_monthAmount')}
                      endAdornment={translatedCurrency}
                      afterBlur={() => calcTotalIncome(formikProps)}
                    />
                  </Grid>
                  <TextField
                    name={`extraIncome[${index}].value.currency`}
                    type="hidden"
                    style={{ visibility: 'hidden' }}
                    value={currency}
                  />
                </Grid>
                <Grid item container>
                  <Grid item className={classes.description}>
                    <FormTextField
                      //onBlur={submitForm}
                      name={`extraIncome[${index}].title`}
                      label={t('finQuestionnary_pension_description')}
                    />
                  </Grid>
                  <Grid item className={classes.deleteButton} style={{ marginBottom: 16 }}>
                    <IconButton
                      size="medium"
                      className="retirement-remove-income-button"
                      onClick={() => {
                        arrayHelpers.remove(index);
                        const newArray = values.extraIncome ? values.extraIncome.filter((_, i) => i !== index) : [];
                        calcTotalIncome({
                          ...formikProps,
                          values: { ...values, extraIncome: newArray },
                        });
                      }}
                    >
                      <DeleteIcon fontSize="inherit" />
                    </IconButton>
                  </Grid>
                </Grid>
              </React.Fragment>
            ))}
            <Button
              className={classes.nextButton}
              variant="outlined"
              style={{ justifySelf: 'start', alignSelf: 'center' }}
              id="retirement-add-income-button"
              onClick={(e: React.MouseEvent) => {
                e.preventDefault();
                arrayHelpers.push({
                  value: defaultValue(currency),
                });
              }}
            >
              {t('finQuestionnary_pension_addMore')}
            </Button>
          </div>
        </>
      )}
    />
  );
};

export default OtherIncome;
